// Corpse economy substrate (R2): marks are CREATED in Sim.killSquad (deterministic,
// mid-system). This module owns decay, Core absorption, and Soul Mill conversion,
// and is the single consumption API — every consumer calls consumeMarksNear.

import { factionDef } from '../../content/factions';
import { buildingDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import { dist, type Vec2 } from '../../shared/vec';
import type { Sim } from '../sim';

const dt = TUNING.dt;

const CORE_HARVEST_RADIUS = 22; // Harvest the Fallen (AH-01)
const CORE_STORAGE_BASE = 8;

export function stepCorpse(sim: Sim): void {
  const marks = sim.state.corpseMarks;
  if (marks.length === 0) return;
  for (let i = marks.length - 1; i >= 0; i--) {
    marks[i].remaining -= dt;
    if (marks[i].remaining <= 0) marks.splice(i, 1);
  }
  if (!sim.corpseEconomyActive || marks.length === 0) return;

  for (const p of sim.state.players) {
    if (!factionDef(p.faction).corpseEconomy) continue;
    // Harvest the Fallen: marks near the Core are absorbed into Throne storage
    const core = sim.coreAlive(p.id);
    if (core) {
      const cap = CORE_STORAGE_BASE + sim.annexBonus(p.id, 'corpseStorageBonus');
      while (p.corpseStored < cap) {
        const got = consumeMarksNear(sim, core.pos, 1, CORE_HARVEST_RADIUS);
        if (got === 0) break;
        p.corpseStored++;
      }
    }
    // Black Harvest (Soul Mill): local marks convert to Essence at the threshold.
    // Mills are processed in ascending id order — the same death never counts twice.
    for (const b of sim.state.buildings.values()) {
      if (b.owner !== p.id || b.state !== 'active') continue;
      const conv = buildingDef(b.defId).corpseConversion;
      if (!conv) continue;
      const consumed = consumeMarksNear(sim, b.pos, 99, conv.radius);
      b.counter += consumed;
      const beforeEchoes = b.traitCounters.millProcessed ?? 0;
      const afterEchoes = beforeEchoes + consumed;
      b.traitCounters.millProcessed = afterEchoes;
      if (b.upgradeDone) {
        const shades = Math.floor(afterEchoes / 10) - Math.floor(beforeEchoes / 10);
        for (let i = 0; i < shades; i++) sim.spawnSquad('u-ah-shade', b.owner, b.pos, { temporarySeconds: 30 }); // TEST
      }
      while (b.counter >= conv.threshold) {
        b.counter -= conv.threshold;
        p.essence += conv.essence;
      }
    }
  }
}

/**
 * Consume up to n marks within radius of pos; returns the number consumed.
 * Deterministic order: nearest first, ties by ascending id.
 */
export function consumeMarksNear(sim: Sim, pos: Vec2, n: number, radius: number): number {
  const marks = sim.state.corpseMarks;
  const eligible = marks
    .map((m, idx) => ({ m, idx, d: dist(m.pos, pos) }))
    .filter((e) => e.d <= radius)
    .sort((a, b) => a.d - b.d || a.m.id - b.m.id)
    .slice(0, n);
  // remove by descending array index so earlier splices don't shift later ones
  for (const e of eligible.sort((a, b) => b.idx - a.idx)) marks.splice(e.idx, 1);
  return eligible.length;
}

/** Count marks within radius (UI/AI queries; no consumption). */
export function countMarksNear(sim: Sim, pos: Vec2, radius: number): number {
  let n = 0;
  for (const m of sim.state.corpseMarks) if (dist(m.pos, pos) <= radius) n++;
  return n;
}
